import React, { useCallback, useEffect, useState } from "react";
import { stat } from "@/lib/i18nFormat";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Clock, CalendarOff, LifeBuoy, ArrowLeftRight, CheckCircle2, Bell } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SHIFT_STATUS: Record<string, string> = { scheduled: "good", covered: "info", callout: "critical" };

export default function MySchedule() {
  const {
    t
  } = useI18n();

  const [profile, setProfile] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("shifts");

  useEffect(() => {
    api.get("/scheduling/me/profile").then((r) => setProfile(r.data.profile)).finally(() => setLoaded(true));
  }, []);

  if (!loaded) return <LoadingBlock />;
  if (!profile) {
    return (
      <div>
        <PageHeader title="My Schedule" testId="myschedule-header" />
        <EmptyState icon={<CalendarClock size={28} />} title="No workforce profile yet"
          description="Your manager will add you to the store roster to start receiving shifts." />
      </div>
    );
  }

  return (
    (<div>
      <PageHeader title="My Schedule" subtitle="Your shifts, availability, time-off and open shifts" testId="myschedule-header" />
      <ScheduleNotifications />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="shifts" data-testid="my-tab-shifts">{t("My Shifts")}</TabsTrigger>
          <TabsTrigger value="open" data-testid="my-tab-open">{t("Open Shifts")}</TabsTrigger>
          <TabsTrigger value="swaps" data-testid="my-tab-swaps">{t("Swaps")}</TabsTrigger>
          <TabsTrigger value="availability" data-testid="my-tab-availability">{t("Availability")}</TabsTrigger>
          <TabsTrigger value="timeoff" data-testid="my-tab-timeoff">{t("Time-off")}</TabsTrigger>
        </TabsList>
        <TabsContent value="shifts"><MyShifts /></TabsContent>
        <TabsContent value="open"><OpenShifts /></TabsContent>
        <TabsContent value="swaps"><Swaps /></TabsContent>
        <TabsContent value="availability"><MyAvailability profile={profile} /></TabsContent>
        <TabsContent value="timeoff"><MyTimeOff /></TabsContent>
      </Tabs>
    </div>)
  );
}

const ScheduleNotifications: React.FC = () => {
  const {
    t
  } = useI18n();

  const [notes, setNotes] = useState<any[]>([]);
  const load = useCallback(() => {
    api.get("/scheduling/me/notifications").then((r) => setNotes(r.data.notifications || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const pending = notes.filter((n) => !n.read || !n.acknowledged_at);
  if (pending.length === 0) return null;
  const dismiss = async () => { await api.post("/scheduling/me/notifications/read", {}).catch(() => {}); setNotes((p) => p.map((n) => ({ ...n, read: true }))); };
  const ack = async (id: string) => {
    await api.post(`/scheduling/me/notifications/${id}/acknowledge`, {}).catch(() => {});
    toast.success("Receipt acknowledged");
    load();
  };
  return (
    (<Card className="mb-4 border-status-info/40 bg-status-info/5 p-3" data-testid="schedule-notifications">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-status-info"><Bell size={15} />{pending.length}{" "}{t("schedule update(s)")}</div>
        <Button size="sm" variant="ghost" onClick={dismiss} data-testid="notifications-dismiss">{t("Mark read")}</Button>
      </div>
      <div className="mt-2 space-y-1.5">
        {pending.slice(0, 5).map((n) => (
          <div key={n.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`schedule-note-${n.id}`}>
            <span className="text-muted-foreground">{n.message}</span>
            {n.acknowledged_at
              ? <span className="flex items-center gap-1 text-status-good"><CheckCircle2 size={13} />{t("acknowledged")}</span>
              : <Button size="sm" variant="outline" className="h-7" onClick={() => ack(n.id)} data-testid={`schedule-note-ack-${n.id}`}>{t("Acknowledge receipt")}</Button>}
          </div>
        ))}
      </div>
    </Card>)
  );
};

const MyShifts: React.FC = () => {
  const {
    t
  } = useI18n();

  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [swapFor, setSwapFor] = useState<any>(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/scheduling/me/shifts").then((r) => setShifts(r.data.shifts)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <LoadingBlock />;
  if (shifts.length === 0) return <EmptyState icon={<CalendarClock size={26} />} title="No published shifts" description="Your upcoming shifts appear here once schedules are published." />;
  return (
    (<div className="space-y-2" data-testid="my-shifts-list">
      {shifts.map((s) => (
        <Card key={s.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`my-shift-${s.id}`}>
          <div>
            <div className="text-sm font-medium">{s.date} · {s.start}–{s.end}</div>
            <div className="text-xs text-muted-foreground">{t("Store")}{" "}{s.store_number}· {s.role || "any role"}</div>
            {s.operating_hours?.configured && (
              <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`my-shift-hours-${s.id}`}>
                {s.operating_hours.closed
                  ? <span className="text-status-warn">{t("Store closed")}{s.operating_hours.label ? ` · ${s.operating_hours.label}` : ""}</span>
                  : <>{t("Store hours")}{" "}{s.operating_hours.store_open}–{s.operating_hours.store_close}{s.operating_hours.label ? ` · ${s.operating_hours.label}` : ""}</>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={SHIFT_STATUS[s.status] || "info"}>{stat(s.status)}</StatusPill>
            {(s.status === "scheduled" || s.status === "covered") && (
              <Button size="sm" variant="outline" onClick={() => setSwapFor(s)} data-testid={`my-shift-swap-${s.id}`}><ArrowLeftRight size={13} className="mr-1" />{t("Offer swap")}</Button>
            )}
          </div>
        </Card>
      ))}
      {swapFor && <SwapRequestDialog shift={swapFor} onClose={() => setSwapFor(null)} onDone={load} />}
    </div>)
  );
};

const SwapRequestDialog: React.FC<{ shift: any; onClose: () => void; onDone: () => void }> = ({ shift, onClose, onDone }) => {
  const {
    t
  } = useI18n();

  const [busy, setBusy] = useState(false);
  const request = async () => {
    setBusy(true);
    try { await api.post(`/scheduling/shifts/${shift.id}/swap-request`, {}); toast.success("Swap offered to eligible colleagues"); onDone(); onClose(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="swap-request-dialog">
        <DialogHeader><DialogTitle>{t("Offer this shift for swap")}</DialogTitle>
          <DialogDescription>{shift.date} · {shift.start}–{shift.end}{t(
            ". An eligible colleague can accept; your manager approves the final swap."
          )}</DialogDescription></DialogHeader>
        <DialogFooter><Button onClick={request} disabled={busy} data-testid="swap-request-confirm">{t("Offer swap")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>)
  );
};

const OpenShifts: React.FC = () => {
  const {
    t
  } = useI18n();

  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/scheduling/open-shifts").then((r) => setShifts(r.data.open_shifts)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const claim = async (id: string) => {
    try { await api.post(`/scheduling/shifts/${id}/claim`, {}); toast.success("Claim submitted for manager approval"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (loading) return <LoadingBlock />;
  if (shifts.length === 0) return <EmptyState icon={<LifeBuoy size={26} />} title="No open shifts for you" description="Open shifts you're eligible for will appear here." />;
  return (
    (<div className="space-y-2" data-testid="open-shifts-list">
      {shifts.map((s) => (
        <Card key={s.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`open-shift-${s.id}`}>
          <div>
            <div className="text-sm font-medium">{s.date} · {s.start}–{s.end}</div>
            <div className="text-xs text-muted-foreground">{t("Store")}{" "}{s.store_number}· {s.role || "any role"}{s.notes ? ` · ${s.notes}` : ""}</div>
          </div>
          {s.my_pending_claim
            ? <StatusPill status="warn" testId={`open-claim-pending-${s.id}`}>{t("claim pending")}</StatusPill>
            : <Button size="sm" onClick={() => claim(s.id)} data-testid={`open-claim-${s.id}`}><CheckCircle2 size={14} className="mr-1" />{t("Claim")}</Button>}
        </Card>
      ))}
    </div>)
  );
};

const Swaps: React.FC = () => {
  const {
    t
  } = useI18n();

  const [swaps, setSwaps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/scheduling/swaps").then((r) => setSwaps(r.data.swaps)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const accept = async (id: string) => {
    try { await api.post(`/scheduling/swaps/${id}/accept`, {}); toast.success("Accepted — pending manager approval"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (loading) return <LoadingBlock />;
  if (swaps.length === 0) return <EmptyState icon={<ArrowLeftRight size={26} />} title="No swaps" description="Swaps you requested or can accept appear here." />;
  return (
    (<div className="space-y-2" data-testid="swaps-list">
      {swaps.map((s) => (
        <Card key={s.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`swap-item-${s.id}`}>
          <div className="text-sm">
            <span className="font-medium">{s.requester_name}</span>{t("'s shift ·")}{" "}{s.shift?.date} {s.shift?.start}–{s.shift?.end}
            <div className="text-xs text-muted-foreground">{s.role || ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={s.status === "accepted" ? "warn" : s.status === "approved" ? "good" : "info"}>{stat(s.status)}</StatusPill>
            {s.can_accept && <Button size="sm" onClick={() => accept(s.id)} data-testid={`swap-accept-${s.id}`}>{t("Accept")}</Button>}
          </div>
        </Card>
      ))}
    </div>)
  );
};

const MyAvailability: React.FC<{ profile: any }> = ({ profile }) => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>(DAYS.map((_, i) => ({ weekday: i, enabled: false, start: "09:00", end: "17:00" })));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/scheduling/me/profile").then((r) => {
      const weekly = r.data.availability?.weekly || [];
      setRows(DAYS.map((_, i) => {
        const w = weekly.find((x: any) => x.weekday === i);
        return w ? { weekday: i, enabled: true, start: w.start, end: w.end } : { weekday: i, enabled: false, start: "09:00", end: "17:00" };
      }));
    }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/scheduling/me/availability", {
        weekly: rows.filter((r) => r.enabled).map((r) => ({ weekday: r.weekday, start: r.start, end: r.end })),
        exceptions: [],
      });
      toast.success("Availability saved");
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<Card className="max-w-lg p-4" data-testid="my-availability">
      <div className="mb-3 flex items-center gap-2 font-display text-sm font-semibold"><Clock size={16} className="text-brand" />{t("When can you work?")}</div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`my-avail-row-${i}`}>
            <label className="flex w-24 items-center gap-2 text-sm">
              <input type="checkbox" checked={r.enabled} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))} data-testid={`my-avail-enable-${i}`} />
              {DAYS[i]}
            </label>
            <Input type="time" className="w-32" value={r.start} disabled={!r.enabled} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} />
            <span>–</span>
            <Input type="time" className="w-32" value={r.end} disabled={!r.enabled} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} />
          </div>
        ))}
      </div>
      <Button className="mt-4" onClick={save} disabled={busy} data-testid="my-avail-save">{t("Save availability")}</Button>
    </Card>)
  );
};

const MyTimeOff: React.FC = () => {
  const {
    t: tI18n
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/scheduling/timeoff?mine=true").then((r) => setRows(r.data.timeoff)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!start || !end) { toast.error("Pick start and end dates"); return; }
    setBusy(true);
    try { await api.post("/scheduling/timeoff", { start_date: start, end_date: end, reason }); toast.success("Time-off requested"); setStart(""); setEnd(""); setReason(""); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    (<div className="space-y-4">
      <Card className="max-w-lg p-4" data-testid="timeoff-form">
        <div className="mb-3 flex items-center gap-2 font-display text-sm font-semibold"><CalendarOff size={16} className="text-status-warn" />{tI18n("Request time-off")}</div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>{tI18n("Start")}</Label><Input type="date" className="mt-1" value={start} onChange={(e) => setStart(e.target.value)} data-testid="timeoff-start" /></div>
          <div><Label>{tI18n("End")}</Label><Input type="date" className="mt-1" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="timeoff-end" /></div>
        </div>
        <div className="mt-2"><Label>{tI18n("Reason (optional)")}</Label><Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="timeoff-reason" /></div>
        <Button className="mt-3" onClick={submit} disabled={busy} data-testid="timeoff-submit">{tI18n("Submit request")}</Button>
      </Card>
      <div>
        <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{tI18n("My requests")}</div>
        {rows.length === 0 ? <div className="text-sm text-muted-foreground">{tI18n("No requests yet.")}</div> : (
          <div className="space-y-2" data-testid="my-timeoff-list">
            {rows.map((t) => (
              <Card key={t.id} className="flex items-center justify-between p-3">
                <div className="text-sm">{t.start_date} → {t.end_date}{t.reason ? ` · ${t.reason}` : ""}</div>
                <StatusPill status={t.status === "approved" ? "good" : t.status === "denied" ? "critical" : "warn"}>{stat(t.status)}</StatusPill>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>)
  );
};
